// src/pages/OffresList.js - VERSION SANS STATISTIQUES DE QUOTAS
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Déclarer COLORS en premier (avant le composant)
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// Types d'offres disponibles
const TYPE_OPTIONS = [
  { value: '', label: 'Tous les types' },
  { value: 'annuel', label: 'Annuel' },
  { value: 'mensuel', label: 'Mensuel' },
  { value: 'trimestriel', label: 'Trimestriel' },
  { value: 'semestriel', label: 'Semestriel' }
];

// Types de tags
const TAG_TYPE_OPTIONS = [
  { value: '', label: 'Tous les tags' },
  { value: 'qr', label: 'QR Code uniquement' },
  { value: 'nfc', label: 'NFC uniquement' },
  { value: 'both', label: 'QR + NFC' }
];

// Options de tri
const ORDER_OPTIONS = [
  { value: 'duree_asc', label: 'Durée (croissant)' },
  { value: 'duree_desc', label: 'Durée (décroissant)' },
  { value: 'prix_asc', label: 'Prix (croissant)' },
  { value: 'prix_desc', label: 'Prix (décroissant)' },
  { value: 'recent', label: 'Plus récent' },
  { value: 'clients_desc', label: 'Clients max (décroissant)' },
  { value: 'clients_asc', label: 'Clients max (croissant)' },
  { value: 'sites_desc', label: 'Sites max (décroissant)' },
  { value: 'sites_asc', label: 'Sites max (croissant)' }
];

const OffresList = () => {
  const navigate = useNavigate();
  const [offres, setOffres] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterTypeTags, setFilterTypeTags] = useState("");
  const [filterPrixMax, setFilterPrixMax] = useState("");
  const [filterClientsMin, setFilterClientsMin] = useState("");
  const [filterSitesMin, setFilterSitesMin] = useState("");
  const [filterTagsMin, setFilterTagsMin] = useState("");
  const [orderBy, setOrderBy] = useState("duree_asc");
  const [showInactive, setShowInactive] = useState(false);
  
  // Récupérer le profil utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentProfile(String(userData.profile || "").trim());
      } catch (error) {
        console.error("Erreur parsing user:", error);
      }
    }
  }, []);

  // Formater le prix en FCFA
  const formatPrix = (montant) => {
    const montantNum = parseFloat(montant) || 0;
    const montantArrondi = Math.round(montantNum);
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  // Formater les nombres
  const formatNombre = (nombre) => {
    return new Intl.NumberFormat('fr-FR').format(nombre || 0);
  };

  // Calculer le prix mensuel
  const calculerPrixMensuel = (montant, dureeMois) => {
    if (dureeMois === 0) return 0;
    return montant / dureeMois;
  };

  // Obtenir l'icône selon le type
  const getTypeIcon = (type) => {
    switch(type) {
      case 'annuel': return 'bi-calendar-year';
      case 'mensuel': return 'bi-calendar-month';
      case 'trimestriel': return 'bi-calendar3';
      case 'semestriel': return 'bi-calendar2-range';
      default: return 'bi-calendar';
    }
  };

  // Obtenir la couleur selon le type
  const getTypeColor = (type) => {
    switch(type) {
      case 'annuel': return '#0B3C5D';
      case 'mensuel': return '#1E88C8';
      case 'trimestriel': return '#4FC3F7';
      case 'semestriel': return '#2196f3';
      default: return '#6c757d';
    }
  };

  // Formater la durée
  const formatDuree = (dureeMois) => {
    if (dureeMois === 1) return '1 mois';
    if (dureeMois < 12) return `${dureeMois} mois`;
    if (dureeMois === 12) return '1 an';
    const annees = Math.floor(dureeMois / 12);
    const mois = dureeMois % 12;
    if (mois === 0) return `${annees} an${annees > 1 ? 's' : ''}`;
    return `${annees} an${annees > 1 ? 's' : ''} et ${mois} mois`;
  };

  // Obtenir le label du type de tags
  const getTypeTagsLabel = (type) => {
    switch(type) {
      case 'qr': return 'QR Code';
      case 'nfc': return 'NFC';
      case 'both': return 'QR + NFC';
      default: return 'Non spécifié';
    }
  };

  // Charger les offres en tenant compte de la logique du backend
  const fetchOffres = useCallback(async () => {
    setLoading(true);
    setServerError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Token d'authentification manquant.");
      setLoading(false);
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      
      // Ajouter les filtres selon la logique du backend
      if (search) params.append("search", search);
      if (filterType) params.append("type", filterType);
      if (filterTypeTags) params.append("type_tags", filterTypeTags);
      if (filterPrixMax) params.append("prix_max", filterPrixMax);
      if (filterClientsMin) params.append("clients_min", filterClientsMin);
      if (filterSitesMin) params.append("sites_min", filterSitesMin);
      if (filterTagsMin) params.append("tags_min", filterTagsMin);
      if (orderBy) params.append("order_by", orderBy);
      
      // Logique selon le backend :
      const isAdmin = currentProfile === "1";
      
      if (isAdmin) {
        // Admin voit par défaut toutes les offres (actives + inactives)
        if (!showInactive) {
          // Si showInactive est false, admin veut voir seulement les actives
          params.append("show_only_active", "true");
        }
      }
      
      const url = `${API_BASE_URL}/offres/list.php?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        setOffres(response.data.offres || []);
        
        if (response.data.offres?.length === 0) {
          setInfoMsg("Aucune offre trouvée avec les critères sélectionnés.");
        } else {
          setInfoMsg("");
        }
      } else {
        setServerError(response.data?.message || "Erreur de chargement.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Accès interdit. Permissions insuffisantes.");
      } else if (!err.response) {
        setServerError("Serveur indisponible.");
      } else {
        setServerError(`Erreur: ${err.response.status}`);
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterTypeTags, filterPrixMax, filterClientsMin, filterSitesMin, filterTagsMin, orderBy, showInactive, currentProfile, navigate]);

  useEffect(() => {
    fetchOffres();
  }, [fetchOffres]);

  // Gérer la recherche
  const handleSearch = (e) => {
    e.preventDefault();
    fetchOffres();
  };

  // Effacer les filtres
  const clearFilters = () => {
    setSearch("");
    setFilterType("");
    setFilterTypeTags("");
    setFilterPrixMax("");
    setFilterClientsMin("");
    setFilterSitesMin("");
    setFilterTagsMin("");
    setOrderBy("duree_asc");
    setShowInactive(false);
    fetchOffres();
  };

  // Supprimer une offre
  const handleDelete = async (id, libele) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'offre "${libele}" ?`)) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/offres/delete.php`;
      const response = await axios.post(
        url,
        { id_offre: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setInfoMsg(`Offre "${libele}" supprimée avec succès.`);
        fetchOffres();
      } else {
        setServerError(response.data?.message || "Échec de la suppression.");
      }
    } catch (err) {
      console.error("Erreur détaillée:", err);
      
      if (err.response?.data?.message) {
        setServerError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setServerError(err.response.data.error);
      } else {
        setServerError("Erreur technique lors de la suppression.");
      }
    }
  };

  // Changer statut offre
  const handleChangeStatus = async (id, libele, currentStatut) => {
    const newStatut = currentStatut === 1 ? 0 : 1;
    const action = newStatut === 1 ? "activer" : "désactiver";
    
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'offre "${libele}" ?`)) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/offres/changer_status.php`;
      const response = await axios.post(
        url,
        { id_offre: id, statut_offre: newStatut },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        const result = newStatut === 1 ? "activée" : "désactivée";
        setInfoMsg(`Offre "${libele}" ${result} avec succès.`);
        fetchOffres();
      } else {
        setServerError(response.data?.message || "Échec de l'opération.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setServerError("Erreur technique.");
    }
  };

  // Calculer les statistiques (uniquement les infos générales)
  const calculerStatistiques = () => {
    if (offres.length === 0) {
      return {
        nombreOffres: 0,
        valeurTotale: 0,
        dureeTotale: 0,
        prixMoyen: 0,
        actives: 0,
        inactives: 0
      };
    }

    const valeurTotale = offres.reduce((total, offre) => {
      return total + (parseFloat(offre.montant) || 0);
    }, 0);

    const dureeTotale = offres.reduce((total, offre) => {
      return total + (parseInt(offre.duree_mois) || 0);
    }, 0);

    const actives = offres.filter(o => o.statut_offre === 1).length;
    const inactives = offres.filter(o => o.statut_offre === 0).length;
    const prixMoyen = offres.length > 0 ? valeurTotale / offres.length : 0;

    return {
      nombreOffres: offres.length,
      valeurTotale,
      dureeTotale,
      prixMoyen,
      actives,
      inactives
    };
  };

  const statistiques = calculerStatistiques();

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Catalogue des offres</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Offres d'abonnement</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-tags"></i>
                    </div>
                    {currentProfile === "1" && showInactive ? "Toutes les offres" : "Offres disponibles"}
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  {currentProfile === "1" && showInactive 
                    ? "Consultez toutes les offres (actives et inactives)" 
                    : "Consultez les offres d'abonnement disponibles pour les sociétés"}
                </p>
              </div>
              
              {currentProfile === "1" && (
                <Link
                  to="/offres/create"
                  className="btn fw-semibold"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.borderColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i> 
                  Nouvelle offre
                </Link>
              )}
            </div>

            {/* Filtres avec quotas */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <form onSubmit={handleSearch} className="row g-3">
                      {/* Ligne 1 : Recherche, Type, Type Tags */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher une offre..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-filter"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {TYPE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-qr-code"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterTypeTags}
                            onChange={(e) => setFilterTypeTags(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {TAG_TYPE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-currency-exchange"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Prix max (FCFA)"
                            value={filterPrixMax}
                            onChange={(e) => setFilterPrixMax(e.target.value)}
                            min="0"
                            step="100"
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="btn fw-semibold"
                            style={{
                              backgroundColor: COLORS.primary,
                              borderColor: COLORS.primary,
                              color: COLORS.white,
                              borderRadius: '8px',
                              padding: '10px 20px',
                              flex: 1,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = COLORS.secondary;
                              e.target.style.borderColor = COLORS.secondary;
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = COLORS.primary;
                              e.target.style.borderColor = COLORS.primary;
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            <i className="bi bi-search me-2"></i> 
                            Appliquer
                          </button>
                        </div>
                      </div>
                    </form>
                    
                    {/* Ligne 2 : Filtres quotas et Tri */}
                    <div className="row mt-3">
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-people"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Clients min"
                            value={filterClientsMin}
                            onChange={(e) => setFilterClientsMin(e.target.value)}
                            min="0"
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-building"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Sites min"
                            value={filterSitesMin}
                            onChange={(e) => setFilterSitesMin(e.target.value)}
                            min="0"
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-tags"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Tags min"
                            value={filterTagsMin}
                            onChange={(e) => setFilterTagsMin(e.target.value)}
                            min="0"
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-sort-down"></i>
                          </span>
                          <select
                            className="form-select"
                            value={orderBy}
                            onChange={(e) => setOrderBy(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {ORDER_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Bouton pour afficher les offres inactives (admin seulement) */}
                      {currentProfile === "1" && (
                        <div className="col-md-3">
                          <div className="form-check form-switch d-flex align-items-center h-100">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              id="showInactive"
                              checked={showInactive}
                              onChange={(e) => setShowInactive(e.target.checked)}
                              style={{
                                width: '3em',
                                height: '1.5em',
                                cursor: 'pointer'
                              }}
                            />
                            <label 
                              className="form-check-label" 
                              htmlFor="showInactive"
                              style={{ color: COLORS.darkText, cursor: 'pointer' }}
                            >
                              Afficher inactives
                            </label>
                          </div>
                        </div>
                      )}
                      
                      <div className="col-md-12 mt-2">
                        <button
                          type="button"
                          className="btn fw-semibold w-100"
                          onClick={clearFilters}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            padding: '10px 24px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.color = COLORS.white;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = COLORS.primary;
                          }}
                        >
                          <i className="bi bi-x-lg me-2"></i>
                          Effacer tous les filtres
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
                <div className="mt-3">
                  <button 
                    className="btn btn-sm fw-semibold"
                    onClick={() => fetchOffres()}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '6px 16px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = COLORS.white;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> 
                    Réessayer
                  </button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-info alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e3f2fd',
                borderColor: COLORS.secondary,
                color: COLORS.primary,
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.secondary}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des offres...</p>
              </div>
            ) : (
              <>
                {offres.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-tag fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucune offre trouvée</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      {currentProfile === "1" && showInactive 
                        ? "Aucune offre (active ou inactive) ne correspond à vos critères." 
                        : "Aucune offre active ne correspond à vos critères de recherche."}
                    </p>
                    {(search || filterType || filterTypeTags || filterPrixMax || filterClientsMin || filterSitesMin || filterTagsMin || showInactive) && (
                      <button
                        className="btn fw-semibold mt-3"
                        onClick={clearFilters}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-lg me-2"></i>
                        Effacer les filtres
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="row g-4">
                      {offres.map((offre) => {
                        const prixMensuel = calculerPrixMensuel(offre.montant, offre.duree_mois);
                        const typeColor = getTypeColor(offre.type_offre);
                        const estInactive = offre.statut_offre === 0;
                        const typeTagsLabel = getTypeTagsLabel(offre.type_tags);
                        
                        // Calcul du pourcentage d'économie
                        const prixMensuelTheorique = offre.duree_mois > 1 ? (offre.montant / offre.duree_mois) : offre.montant;
                        const economiePourcentage = offre.duree_mois > 1 
                          ? Math.round((1 - (prixMensuel / prixMensuelTheorique)) * 100)
                          : 0;
                        
                        return (
                          <div key={offre.id_offre} className="col-md-6">
                            <div className="card h-100" style={{ 
                              border: `1px solid ${estInactive ? '#ffcdd2' : '#e0e0e0'}`,
                              borderRadius: '12px',
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              opacity: estInactive ? 0.85 : 1,
                              backgroundColor: estInactive ? '#fff8f8' : COLORS.white
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              {/* En-tête de la carte */}
                              <div className="card-header border-0 pb-0" style={{ 
                                backgroundColor: estInactive ? '#B0BEC5' : typeColor,
                                color: COLORS.white,
                                padding: '20px'
                              }}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h5 className="card-title mb-1" style={{ 
                                      color: COLORS.white,
                                      fontSize: '1.1rem',
                                      fontWeight: '600'
                                    }}>
                                      {offre.libele_offre}
                                    </h5>
                                    <div className="d-flex align-items-center">
                                      <i className={`bi ${getTypeIcon(offre.type_offre)} me-2`}></i>
                                      <span className="badge fw-semibold" style={{ 
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        color: COLORS.white,
                                        fontSize: '0.75rem',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase'
                                      }}>
                                        {offre.type_offre}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {estInactive && (
                                    <span className="badge fw-semibold" style={{ 
                                      backgroundColor: COLORS.warning,
                                      color: '#212529',
                                      fontSize: '0.7rem',
                                      padding: '4px 8px',
                                      borderRadius: '4px'
                                    }}>
                                      <i className="bi bi-eye-slash me-1"></i>
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Corps de la carte */}
                              <div className="card-body pt-4">
                                <div className="mb-4">
                                  <p className="card-text" style={{ 
                                    color: COLORS.darkText,
                                    fontSize: '0.9rem',
                                    minHeight: '60px'
                                  }}>
                                    {offre.description || "Aucune description disponible."}
                                  </p>
                                </div>
                                
                                <div className="row mb-4">
                                  <div className="col-6">
                                    <div className="text-center p-3 rounded" style={{ 
                                      backgroundColor: '#E3F2FD',
                                      border: `1px solid ${COLORS.secondary}20`
                                    }}>
                                      <div className="mb-1">
                                        <i className="bi bi-calendar-check" style={{ 
                                          color: COLORS.secondary,
                                          fontSize: '1.2rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold" style={{ 
                                        color: COLORS.primary,
                                        fontSize: '1.1rem'
                                      }}>
                                        {formatDuree(offre.duree_mois)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        Durée
                                      </small>
                                    </div>
                                  </div>
                                  
                                  <div className="col-6">
                                    <div className="text-center p-3 rounded" style={{ 
                                      backgroundColor: '#E8F5E9',
                                      border: `1px solid ${COLORS.success}20`
                                    }}>
                                      <div className="mb-1">
                                        <i className="bi bi-cash" style={{ 
                                          color: COLORS.success,
                                          fontSize: '1.2rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold" style={{ 
                                        color: '#2e7d32',
                                        fontSize: '1.1rem'
                                      }}>
                                        {formatPrix(prixMensuel)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        Par mois
                                      </small>
                                    </div>
                                  </div>
                                </div>

                                {/* Quotas */}
                                <div className="row mb-4">
                                  <div className="col-4 text-center">
                                    <div className="p-2">
                                      <i className="bi bi-people" style={{ color: COLORS.info }}></i>
                                      <div className="fw-bold" style={{ fontSize: '1rem' }}>
                                        {formatNombre(offre.max_clients)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        Clients
                                      </small>
                                    </div>
                                  </div>
                                  <div className="col-4 text-center">
                                    <div className="p-2">
                                      <i className="bi bi-building" style={{ color: COLORS.info }}></i>
                                      <div className="fw-bold" style={{ fontSize: '1rem' }}>
                                        {formatNombre(offre.max_sites)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        Sites
                                      </small>
                                    </div>
                                  </div>
                                  <div className="col-4 text-center">
                                    <div className="p-2">
                                      <i className={`bi ${offre.type_tags === 'qr' ? 'bi-qr-code' : offre.type_tags === 'nfc' ? 'bi-wifi' : 'bi-tags'}`} 
                                        style={{ color: COLORS.info }}></i>
                                      <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                        {typeTagsLabel}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        {offre.max_tags_qr > 0 && `QR: ${formatNombre(offre.max_tags_qr)}`}
                                        {offre.max_tags_qr > 0 && offre.max_tags_nfc > 0 && ' | '}
                                        {offre.max_tags_nfc > 0 && `NFC: ${formatNombre(offre.max_tags_nfc)}`}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="mb-4">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                                      <i className="bi bi-cash-stack me-1"></i>
                                      Prix total
                                    </span>
                                    <span className="fw-bold" style={{ 
                                      color: COLORS.primary,
                                      fontSize: '1.3rem'
                                    }}>
                                      {formatPrix(offre.montant)}
                                    </span>
                                  </div>
                                  
                                  <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ 
                                        width: '100%',
                                        backgroundColor: typeColor
                                      }}
                                      aria-valuenow="100" 
                                      aria-valuemin="0" 
                                      aria-valuemax="100"
                                    ></div>
                                  </div>
                                  
                                  <div className="d-flex justify-content-between mt-1">
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      Économie : {economiePourcentage}%
                                    </small>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      {offre.duree_mois} mois
                                    </small>
                                  </div>
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div>
                                    <span className={`badge fw-semibold ${offre.statut_offre === 1 ? 'bg-success' : 'bg-secondary'}`} style={{ 
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      fontSize: '0.8rem'
                                    }}>
                                      <i className={`bi ${offre.statut_offre === 1 ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                      {offre.statut_offre === 1 ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                  
                                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {new Date(offre.created_at).toLocaleDateString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                  </small>
                                </div>
                              </div>
                              
                              {/* Pied de la carte */}
                              <div className="card-footer border-0 pt-0 pb-3">
                                <div className="d-flex gap-2">
                                  <Link
                                    to={`/offres/details/${offre.id_offre}`}
                                    className="btn fw-semibold"
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '8px',
                                      padding: '8px 16px',
                                      flex: 1,
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.color = COLORS.white;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = COLORS.primary;
                                    }}
                                  >
                                    <i className="bi bi-eye me-2"></i>
                                    Détails
                                  </Link>
                                  
                                  {currentProfile === "1" && (
                                    <>
                                      <button
                                        className="btn fw-semibold"
                                        onClick={() => handleChangeStatus(offre.id_offre, offre.libele_offre, offre.statut_offre)}
                                        title={offre.statut_offre === 1 ? "Désactiver" : "Activer"}
                                        style={{
                                          color: offre.statut_offre === 1 ? COLORS.warning : COLORS.success,
                                          borderColor: offre.statut_offre === 1 ? COLORS.warning : COLORS.success,
                                          backgroundColor: 'transparent',
                                          borderRadius: '8px',
                                          padding: '8px',
                                          width: '40px',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = offre.statut_offre === 1 ? COLORS.warning : COLORS.success;
                                          e.target.style.color = COLORS.white;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.color = offre.statut_offre === 1 ? COLORS.warning : COLORS.success;
                                        }}
                                      >
                                        <i className={`bi ${offre.statut_offre === 1 ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                                      </button>
                                      
                                      <button
                                        className="btn fw-semibold"
                                        onClick={() => handleDelete(offre.id_offre, offre.libele_offre)}
                                        title="Supprimer"
                                        style={{
                                          color: COLORS.danger,
                                          borderColor: COLORS.danger,
                                          backgroundColor: 'transparent',
                                          borderRadius: '8px',
                                          padding: '8px',
                                          width: '40px',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = COLORS.danger;
                                          e.target.style.color = COLORS.white;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.color = COLORS.danger;
                                        }}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Statistiques générales uniquement */}
                    <div className="row mt-5">
                      <div className="col-12">
                        <div className="card" style={{ 
                          backgroundColor: '#F5F7FA',
                          border: 'none',
                          borderRadius: '12px'
                        }}>
                          <div className="card-body py-3">
                            <div className="row text-center">
                              <div className="col-md-2">
                                <div className="p-2">
                                  <div className="fw-bold" style={{ 
                                    color: COLORS.primary,
                                    fontSize: '1.5rem'
                                  }}>
                                    {statistiques.nombreOffres}
                                  </div>
                                  <small className="text-muted">
                                    {currentProfile === "1" && showInactive ? 'Total offres' : 'Offres actives'}
                                  </small>
                                </div>
                              </div>
                              
                              {currentProfile === "1" && showInactive && (
                                <>
                                  <div className="col-md-2">
                                    <div className="p-2">
                                      <div className="fw-bold" style={{ 
                                        color: COLORS.success,
                                        fontSize: '1.5rem'
                                      }}>
                                        {statistiques.actives}
                                      </div>
                                      <small className="text-muted">Actives</small>
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-2">
                                    <div className="p-2">
                                      <div className="fw-bold" style={{ 
                                        color: COLORS.warning,
                                        fontSize: '1.5rem'
                                      }}>
                                        {statistiques.inactives}
                                      </div>
                                      <small className="text-muted">Inactives</small>
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              <div className="col-md-2">
                                <div className="p-2">
                                  <div className="fw-bold" style={{ 
                                    color: COLORS.secondary,
                                    fontSize: '1.5rem'
                                  }}>
                                    {formatPrix(statistiques.valeurTotale)}
                                  </div>
                                  <small className="text-muted">Valeur totale</small>
                                </div>
                              </div>
                              
                              <div className="col-md-2">
                                <div className="p-2">
                                  <div className="fw-bold" style={{ 
                                    color: '#4FC3F7',
                                    fontSize: '1.5rem'
                                  }}>
                                    {statistiques.dureeTotale} mois
                                  </div>
                                  <small className="text-muted">Durée totale</small>
                                </div>
                              </div>
                              
                              <div className="col-md-2">
                                <div className="p-2">
                                  <div className="fw-bold" style={{ 
                                    color: '#2e7d32',
                                    fontSize: '1.5rem'
                                  }}>
                                    {formatPrix(statistiques.prixMoyen)}
                                  </div>
                                  <small className="text-muted">Prix moyen</small>
                                </div>
                              </div>
                            </div>
                            
                            {/* Résumé */}
                            <div className="mt-3 text-center">
                              <small className="text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                {currentProfile === "1" && showInactive 
                                  ? `Affichage : ${statistiques.actives} offres actives + ${statistiques.inactives} offres inactives` 
                                  : `Affichage : ${statistiques.actives} offres actives seulement`}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .card {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-check-input:checked {
          background-color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
        
        .form-check-input:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .row.g-4 {
            margin: 0 -8px;
          }
          
          .col-md-6 {
            padding: 0 8px;
          }
          
          .card-body .row .col-6 {
            padding: 0 8px;
          }
          
          .d-flex.gap-2 {
            justify-content: center;
            flex-wrap: wrap;
          }
          
          .btn {
            margin-bottom: 8px;
          }
          
          .row .col-md-3, .row .col-md-4, .row .col-md-2 {
            margin-bottom: 16px;
          }
        }
        
        @media (max-width: 576px) {
          .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
          
          .card-header h5 {
            font-size: 1rem;
          }
          
          .card-body .fw-bold {
            font-size: 1rem;
          }
        }
        
        /* Amélioration de la lisibilité */
        .progress {
          overflow: visible;
        }
        
        .progress-bar {
          position: relative;
          overflow: visible;
        }
        
        .progress-bar::after {
          content: '';
          position: absolute;
          right: -4px;
          top: -2px;
          width: 8px;
          height: 8px;
          background-color: inherit;
          border-radius: 50%;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default OffresList;